<template>
  <div class="leave-compliance-addon">
    <div class="addon-head">
      <div>
        <span>HRM_LEAVE_COMPLIANCE_RISK</span>
        <h2>假勤合规风险洞察</h2>
        <p>聚合请假申请、假期额度和部门分布，识别待审积压、超长假、无额度和超额度等合规风险。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">部门合规风险</div>
          <el-table :data="overview.deptComplianceRisk" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="request_count" label="申请" width="80" />
            <el-table-column prop="pending_count" label="待审" width="80" />
            <el-table-column prop="long_leave_count" label="长假" width="80" />
            <el-table-column prop="missing_quota_count" label="额度缺口" width="90" />
            <el-table-column prop="leave_days" label="总天数" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">审批状态分布</div>
          <div class="status-list">
            <div v-for="item in overview.statusDistribution" :key="item.status" class="status-item">
              <span>{{ formatStatus(item.status) }}</span>
              <strong>{{ item.request_count }}</strong>
              <em>{{ item.leave_days || 0 }} 天</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">超长假排行</div>
          <el-table :data="overview.longLeaveRanking" size="small" v-loading="loading">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="type_num" label="类型" width="90">
              <template slot-scope="{ row }">{{ formatLeaveType(row.type_num) }}</template>
            </el-table-column>
            <el-table-column prop="days" label="天数" width="80" />
            <el-table-column prop="status" label="状态" width="90">
              <template slot-scope="{ row }">{{ formatStatus(row.status) }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">额度冲突</div>
          <el-table :data="overview.quotaConflictList" size="small" v-loading="loading">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="type_num" label="类型" width="90">
              <template slot-scope="{ row }">{{ formatLeaveType(row.type_num) }}</template>
            </el-table-column>
            <el-table-column prop="requested_days" label="申请" width="80" />
            <el-table-column prop="quota_days" label="额度" width="80" />
            <el-table-column prop="risk_type" label="风险" width="100">
              <template slot-scope="{ row }">{{ formatRisk(row.risk_type) }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-card class="lower-row" shadow="never">
      <div slot="header">月度假勤趋势</div>
      <div class="trend-list">
        <div v-for="item in overview.monthlyTrend" :key="item.month_label" class="trend-item">
          <span>{{ item.month_label }}</span>
          <strong>{{ item.request_count }}</strong>
          <em>{{ item.leave_days || 0 }} 天 / 待审 {{ item.pending_count || 0 }}</em>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getLeaveComplianceRiskOverview } from '../api/leaveComplianceRisk'

export default {
  name: 'LeaveComplianceRiskOverview',
  data () {
    return {
      loading: false,
      overview: {
        leaveRequestCount: 0,
        pendingLeaveCount: 0,
        approvedLeaveCount: 0,
        rejectedLeaveCount: 0,
        longLeaveCount: 0,
        missingQuotaCount: 0,
        statusDistribution: [],
        typeDistribution: [],
        deptComplianceRisk: [],
        longLeaveRanking: [],
        quotaConflictList: [],
        monthlyTrend: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '请假申请', value: this.overview.leaveRequestCount, meta: `${this.overview.approvedLeaveCount || 0} 条已通过` },
        { label: '待审积压', value: this.overview.pendingLeaveCount, meta: '需及时审批', tone: 'orange' },
        { label: '超长假', value: this.overview.longLeaveCount, meta: '单次 >= 5 天', tone: 'red' },
        { label: '额度缺口', value: this.overview.missingQuotaCount, meta: `${this.overview.rejectedLeaveCount || 0} 条驳回`, tone: 'blue' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getLeaveComplianceRiskOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '假勤合规风险洞察加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    formatStatus (status) {
      const map = { 0: '未审核', 1: '通过', 2: '驳回', 3: '撤销' }
      return map[Number(status)] || '未知'
    },
    formatLeaveType (type) {
      const map = { 0: '事假', 1: '产假', 2: '病假', 3: '婚假', 4: '探亲假', 5: '陪产假' }
      return map[Number(type)] || '未知'
    },
    formatRisk (risk) {
      return risk === 'NO_QUOTA' ? '无额度' : risk === 'OVER_QUOTA' ? '超额度' : '正常'
    }
  }
}
</script>

<style lang="less" scoped>
.leave-compliance-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #7d3c98;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.status-list,
.trend-list {
  display: grid;
  gap: 10px;
}

.status-item,
.trend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.status-item span,
.status-item em,
.trend-item span,
.trend-item em {
  color: #5f6f89;
  font-style: normal;
}

.status-item strong,
.trend-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
